import { LayoutDashboard, Bell, Filter, Zap, Mail, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  BenefitCard,
  AlertCard,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const features = [
  {
    icon: LayoutDashboard,
    title: "Dashboard centralisé",
    description: "Toutes vos alertes en un seul endroit, priorisées automatiquement.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: Filter,
    title: "Filtres intelligents",
    description: "Par type d'alerte, par chantier, par niveau d'urgence.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
  {
    icon: Zap,
    title: "Actions en un clic",
    description: "Traitez chaque alerte directement depuis le centre de pilotage.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: Mail,
    title: "Notifications email",
    description: "Configurez les alertes à recevoir par email selon leur criticité.",
    gradient: "from-[#10B981] to-[#059669]",
  },
];

const CentrePilotageFeature = () => {
  usePageMeta({ title: "Tableau de bord BTP — Centre de pilotage | B8ild", description: "Visualisez tous vos KPI chantier en un coup d'œil. Dashboard de pilotage financier pour artisans et entreprises du BTP.", url: "https://b8ild.fr/product/centre-pilotage" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Tableau de bord BTP — Centre de pilotage | B8ild",
        description: "Visualisez tous vos KPI chantier en un coup d'œil. Dashboard de pilotage financier pour artisans et entreprises du BTP.",
        url: "https://b8ild.fr/product/centre-pilotage",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Centre de pilotage", item: "https://b8ild.fr/product/centre-pilotage" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Centre de pilotage" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Centre de pilotage"
        title="Ne ratez plus aucune"
        titleHighlight="alerte critique."
        subtitle="Toutes les informations importantes sur vos chantiers, centralisées et priorisées. Agissez avant qu'il ne soit trop tard."
        icon={LayoutDashboard}
        gradient="from-[#3B82F6] to-[#2563EB]"
      />

      {/* Alert Types */}
      <FeatureSection
        title="Types d'alertes"
        subtitle="Classées par niveau de criticité"
      >
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6">
          <AlertCard
            level="critical"
            title="CRITIQUE"
            items={[
              "Marge négative (chantier en déficit)",
              "Jour Critique dépassé",
              "Budget dépassé > 100%",
              "Chantier sans équipe (démarre dans < 3 jours)",
            ]}
          />
          <AlertCard
            level="warning"
            title="ATTENTION"
            items={[
              "Marge faible < 18%",
              "Budget consommé > 80%",
              "Retard détecté",
              "Facture impayée > 30 jours",
            ]}
          />
          <AlertCard
            level="info"
            title="INFO"
            items={[
              "Chantier terminé à facturer",
              "Analyse IA disponible",
              "Nouveau chantier créé",
              "Membre équipe absent demain",
            ]}
          />
        </div>
      </FeatureSection>

      {/* Dashboard Preview */}
      <FeatureSection variant="dark" title="Centre de pilotage en action">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-[8px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
                    <Bell className="h-4 w-4 text-white/50" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Centre de pilotage</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    <span className="px-2 py-1 text-xs rounded-full bg-[#EF4444]/10 text-[#EF4444]">2 critiques</span>
                    <span className="px-2 py-1 text-xs rounded-full bg-[#F59E0B]/10 text-[#F59E0B]">5 attention</span>
                    <span className="px-2 py-1 text-xs rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">3 infos</span>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="px-6 py-3 border-b border-white/[0.03] flex items-center gap-4">
                <span className="text-sm text-white/40">Filtrer :</span>
                <button className="px-3 py-1 text-xs bg-white/[0.05] text-white/70 rounded-md">Tous</button>
                <button className="px-3 py-1 text-xs text-white/50 hover:bg-white/[0.05] rounded-md transition-colors">Critiques</button>
                <button className="px-3 py-1 text-xs text-white/50 hover:bg-white/[0.05] rounded-md transition-colors">Attention</button>
                <button className="px-3 py-1 text-xs text-white/50 hover:bg-white/[0.05] rounded-md transition-colors">Info</button>
              </div>

              {/* Alerts list */}
              <div className="divide-y divide-white/[0.03]">
                {/* Critical alert */}
                <div className="px-6 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-[#EF4444]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#EF4444]/10 text-[#EF4444] font-medium">CRITIQUE</span>
                      <span className="text-xs text-white/40">Il y a 15 min</span>
                    </div>
                    <p className="text-white font-medium mt-1">Marge négative sur "Rénovation Martin"</p>
                    <p className="text-sm text-white/50 mt-0.5">La marge est passée à -2% (-850€). Action immédiate requise.</p>
                  </div>
                  <button className="px-3 py-1.5 text-sm bg-[#EF4444]/10 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/20 transition-colors flex-shrink-0">
                    Voir le chantier
                  </button>
                </div>

                {/* Critical alert 2 */}
                <div className="px-6 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-[#EF4444]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#EF4444]/10 text-[#EF4444] font-medium">CRITIQUE</span>
                      <span className="text-xs text-white/40">Il y a 2h</span>
                    </div>
                    <p className="text-white font-medium mt-1">Budget dépassé sur "Villa Durand"</p>
                    <p className="text-sm text-white/50 mt-0.5">Le budget est consommé à 105%. Dépassement de 2 100€.</p>
                  </div>
                  <button className="px-3 py-1.5 text-sm bg-[#EF4444]/10 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/20 transition-colors flex-shrink-0">
                    Voir le chantier
                  </button>
                </div>

                {/* Warning alert */}
                <div className="px-6 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-[#F59E0B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#F59E0B]/10 text-[#F59E0B] font-medium">ATTENTION</span>
                      <span className="text-xs text-white/40">Il y a 4h</span>
                    </div>
                    <p className="text-white font-medium mt-1">Marge faible sur "Extension Petit"</p>
                    <p className="text-sm text-white/50 mt-0.5">La marge actuelle est de 12%. En dessous du seuil recommandé (18%).</p>
                  </div>
                  <button className="px-3 py-1.5 text-sm bg-[#F59E0B]/10 text-[#F59E0B] rounded-lg hover:bg-[#F59E0B]/20 transition-colors flex-shrink-0">
                    Analyser
                  </button>
                </div>

                {/* Info alert */}
                <div className="px-6 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center flex-shrink-0">
                    <Info className="h-5 w-5 text-[#3B82F6]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#3B82F6]/10 text-[#3B82F6] font-medium">INFO</span>
                      <span className="text-xs text-white/40">Hier</span>
                    </div>
                    <p className="text-white font-medium mt-1">Analyse IA disponible pour "Cuisine Bernard"</p>
                    <p className="text-sm text-white/50 mt-0.5">Une nouvelle analyse de rentabilité est prête.</p>
                  </div>
                  <button className="px-3 py-1.5 text-sm bg-[#3B82F6]/10 text-[#3B82F6] rounded-lg hover:bg-[#3B82F6]/20 transition-colors flex-shrink-0">
                    Voir l'analyse
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Features */}
      <FeatureSection
        title="Fonctionnalités du centre de pilotage"
        subtitle="Tout pour garder le contrôle"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <BenefitCard key={index} {...feature} />
          ))}
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              12 chantiers ouverts. Lequel va déraper ?
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Nathalie dirige une entreprise de 12 salaries. 10 a 15 chantiers simultanes. Chaque matin, meme question : lequel va deraper ? Avant, elle passait 1h30 sur Excel pour consolider les chiffres. Et encore, les donnees avaient 2 semaines de retard. Resultat : elle decouvrait les problemes quand ils etaient deja irreparables.
              </p>
              <p>
                Avec un tableau de bord centralise, elle voit en 30 secondes : 3 chantiers au vert (marge &gt; 18%), 5 en jaune (12-18%), 2 en orange (&lt; 12%). Elle sait ou concentrer son attention.
              </p>
              <p>
                Le chantier rue des Lilas est a 8% de marge et consomme du budget plus vite que prevu. Elle appelle son chef d'equipe, identifie le probleme (trop d'heures sur la preparation), et corrige. 45 minutes au lieu de 3 jours de delai. Sur un an : 15 000 a 25 000 EUR de marge recuperee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Un tableau de bord BTP, c'est quoi concretement ?",
                  a: "Un ecran qui montre tous vos chantiers en un coup d'oeil. Pour chacun : le % de marge actuel, le budget consomme vs restant, et un code couleur (vert/jaune/orange/rouge). Vous voyez en 10 secondes lequel va bien et lequel derape. Pas besoin de fouiller dans 5 fichiers Excel.",
                },
                {
                  q: "Quels KPI suivre sur un chantier BTP ?",
                  a: "Les 4 indicateurs cles : marge nette (% et EUR), budget consomme vs budget total, avancement physique vs financier, et jours restants avant le jour critique. Si le financier avance plus vite que le physique, vous perdez de l'argent. C'est l'indicateur n°1.",
                },
                {
                  q: "Comment prioriser quand on gere 10 chantiers ?",
                  a: "Triez par urgence : d'abord les chantiers proches du jour critique (< 7 jours). Ensuite ceux dont la marge est sous 10%. Enfin les chantiers avec des penalites de retard contractuelles. Un chantier a 25% de marge peut attendre. Un chantier a 5% de marge ne peut pas.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Gardez le contrôle"
        subtitle="Toutes vos alertes, au bon moment, au bon endroit."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="centre-pilotage" />

      <LandingFooter />
    </div>
  );
};

export default CentrePilotageFeature;
